// Translations for Swedish Forest Economy App
// Languages: Swedish (sv), German (de), English (en), Northern Sami (se)

export const translations = {
  sv: {
    // App
    appName: "Skogskalkyl",
    appDescription: "Räkna ut vad rätt skötsel verkligen är värd",
    
    // Navigation
    nav: {
      home: "Hem",
      calculator: "Kalkylator",
      dashboard: "Översikt",
      sensitivity: "Känslighetsanalys",
      settings: "Inställningar",
      logout: "Logga ut",
      login: "Logga in",
      register: "Registrera",
      faq: "FAQ",
      about: "Om oss"
    },
    
    // Auth
    auth: {
      email: "E-post",
      password: "Lösenord",
      name: "Namn",
      loginTitle: "Logga in på ditt konto",
      registerTitle: "Skapa ett konto",
      loginButton: "Logga in",
      registerButton: "Registrera",
      noAccount: "Har du inget konto?",
      hasAccount: "Har du redan ett konto?",
      createAccount: "Skapa konto",
      signIn: "Logga in"
    },
    
    // Calculator
    calc: {
      title: "Skogsekonomisk kalkylator",
      subtitle: "Jämför ekonomiska utfall mellan skött och försummad skog",
      scenarioName: "Scenarionamn",
      region: "Region",
      area: "Areal (hektar)",
      period: "Period (år)",
      treeSpecies: "Trädslag",
      growthManaged: "Tillväxt skött (m³/ha/år)",
      growthNeglected: "Tillväxt försummad (m³/ha/år)",
      priceTimber: "Pris sågtimmer (SEK/m³)",
      pricePulpwood: "Pris massaved (SEK/m³)",
      clearingCost: "Röjningskostnad (SEK/ha)",
      plantingCost: "Planteringskostnad (SEK/ha)",
      thinningCost: "Gallringskostnad (SEK/m³)",
      discountRate: "Kalkylränta (%)",
      calculate: "Beräkna",
      preview: "Förhandsvisa",
      saveScenario: "Spara scenario",
      results: "Resultat",
      loadRegionalDefaults: "Ladda regiondata"
    },
    
    // Risk
    risk: {
      title: "Riskscenarier",
      stormProbability: "Stormsannolikhet (%/år)",
      stormDamage: "Stormskada (% av volym)",
      barkBeetleProbability: "Barkborresannolikhet (%/år)",
      barkBeetleDamage: "Barkborreskada (% av volym)",
      fireProbability: "Brandrisk (%/år)",
      fireDamage: "Brandskada (% av volym)",
      enableRisk: "Aktivera riskberäkning"
    },
    
    // Results
    results: {
      title: "Beräkningsresultat",
      summary: "Sammanfattning",
      breakeven: "Brytpunkt",
      breakevenYear: "Brytpunkt uppnås år",
      totalBenefit: "Total nettofördel",
      year: "År",
      volume: "Volym",
      revenue: "Intäkt",
      cost: "Kostnad",
      netBenefit: "Nettofördel",
      cumulativeBenefit: "Kumulativ fördel",
      npv: "Nuvärde",
      managedForest: "Skött skog",
      neglectedForest: "Försummad skog",
      extraVolume: "Extra volym",
      extraRevenue: "Extra intäkt",
      exportPdf: "Exportera PDF",
      noBreakeven: "Ingen brytpunkt inom perioden"
    },
    
    // Species
    species: {
      gran: "Gran (Picea abies)",
      tall: "Tall (Pinus sylvestris)",
      bjork: "Björk (Betula)"
    },
    
    // Regions
    regions: {
      norrland: "Norrland",
      svealand: "Svealand",
      gotaland: "Götaland"
    },
    
    // Dashboard
    dashboard: {
      title: "Dina scenarier",
      noScenarios: "Inga sparade scenarier",
      createFirst: "Skapa ditt första scenario",
      lastUpdated: "Senast uppdaterad",
      delete: "Radera",
      view: "Visa",
      totalScenarios: "Totalt antal scenarier"
    },
    
    // Sensitivity
    sensitivity: {
      title: "Känslighetsanalys",
      description: "Analysera hur pris- och tillväxtförändringar påverkar resultatet",
      priceVariation: "Prisvariation",
      growthVariation: "Tillväxtvariation",
      baseScenario: "Basscenario",
      runAnalysis: "Kör analys"
    },
    
    // Import/Export
    importExport: {
      importCsv: "Importera CSV",
      exportPdf: "Exportera PDF",
      dragDrop: "Dra och släpp CSV-fil här",
      orClick: "eller klicka för att välja fil",
      importSuccess: "Import lyckades",
      importError: "Importfel"
    },
    
    // Common
    common: {
      loading: "Laddar...",
      error: "Ett fel uppstod",
      success: "Klart",
      save: "Spara",
      cancel: "Avbryt",
      delete: "Radera",
      edit: "Redigera",
      close: "Stäng",
      confirm: "Bekräfta",
      back: "Tillbaka",
      next: "Nästa",
      sek: "SEK",
      hectare: "ha",
      year: "år",
      years: "år",
      cubicMeter: "m³"
    },
    
    // Tooltips
    tooltips: {
      growthManaged: "Genomsnittlig årlig tillväxt vid optimal skogsskötsel (röjning, gallring)",
      growthNeglected: "Tillväxt vid utebliven skötsel - typiskt 20-30% lägre",
      breakeven: "Tidpunkt då skötselkostnaderna har återbetalats genom högre avkastning"
    }
  },
  
  de: {
    appName: "Skogskalkyl",
    appDescription: "Berechnen Sie, was richtige Waldpflege wirklich wert ist",
    
    nav: {
      home: "Startseite",
      calculator: "Rechner",
      dashboard: "Übersicht",
      sensitivity: "Sensitivitätsanalyse",
      settings: "Einstellungen",
      logout: "Abmelden",
      login: "Anmelden",
      register: "Registrieren",
      faq: "FAQ",
      about: "Über uns"
    },
    
    auth: {
      email: "E-Mail",
      password: "Passwort",
      name: "Name",
      loginTitle: "In Ihr Konto einloggen",
      registerTitle: "Konto erstellen",
      loginButton: "Anmelden",
      registerButton: "Registrieren",
      noAccount: "Noch kein Konto?",
      hasAccount: "Bereits ein Konto?",
      createAccount: "Konto erstellen",
      signIn: "Anmelden"
    },
    
    calc: {
      title: "Forstwirtschaftlicher Rechner",
      subtitle: "Vergleichen Sie wirtschaftliche Ergebnisse zwischen bewirtschaftetem und vernachlässigtem Wald",
      scenarioName: "Szenarioname",
      region: "Region",
      area: "Fläche (Hektar)",
      period: "Zeitraum (Jahre)",
      treeSpecies: "Baumart",
      growthManaged: "Zuwachs bewirtschaftet (m³/ha/Jahr)",
      growthNeglected: "Zuwachs vernachlässigt (m³/ha/Jahr)",
      priceTimber: "Stammholzpreis (SEK/m³)",
      pricePulpwood: "Industrieholzpreis (SEK/m³)",
      clearingCost: "Läuterungskosten (SEK/ha)",
      plantingCost: "Pflanzkosten (SEK/ha)",
      thinningCost: "Durchforstungskosten (SEK/m³)",
      discountRate: "Kalkulationszins (%)",
      calculate: "Berechnen",
      preview: "Vorschau",
      saveScenario: "Szenario speichern",
      results: "Ergebnisse",
      loadRegionalDefaults: "Regionaldaten laden"
    },
    
    risk: {
      title: "Risikoszenarien",
      stormProbability: "Sturmwahrscheinlichkeit (%/Jahr)",
      stormDamage: "Sturmschaden (% des Volumens)",
      barkBeetleProbability: "Borkenkäferwahrscheinlichkeit (%/Jahr)",
      barkBeetleDamage: "Borkenkäferschaden (% des Volumens)",
      fireProbability: "Brandrisiko (%/Jahr)",
      fireDamage: "Brandschaden (% des Volumens)",
      enableRisk: "Risikoberechnung aktivieren"
    },
    
    results: {
      title: "Berechnungsergebnisse",
      summary: "Zusammenfassung",
      breakeven: "Break-Even-Punkt",
      breakevenYear: "Break-Even erreicht im Jahr",
      totalBenefit: "Gesamtnettonutzen",
      year: "Jahr",
      volume: "Volumen",
      revenue: "Einnahmen",
      cost: "Kosten",
      netBenefit: "Nettonutzen",
      cumulativeBenefit: "Kumulativer Nutzen",
      npv: "Barwert",
      managedForest: "Bewirtschafteter Wald",
      neglectedForest: "Vernachlässigter Wald",
      extraVolume: "Zusätzliches Volumen",
      extraRevenue: "Zusätzliche Einnahmen",
      exportPdf: "PDF exportieren",
      noBreakeven: "Kein Break-Even innerhalb des Zeitraums"
    },
    
    species: {
      gran: "Fichte (Picea abies)",
      tall: "Kiefer (Pinus sylvestris)",
      bjork: "Birke (Betula)"
    },
    
    regions: {
      norrland: "Norrland",
      svealand: "Svealand",
      gotaland: "Götaland"
    },
    
    dashboard: {
      title: "Ihre Szenarien",
      noScenarios: "Keine gespeicherten Szenarien",
      createFirst: "Erstellen Sie Ihr erstes Szenario",
      lastUpdated: "Zuletzt aktualisiert",
      delete: "Löschen",
      view: "Ansehen",
      totalScenarios: "Gesamtszenarien"
    },
    
    sensitivity: {
      title: "Sensitivitätsanalyse",
      description: "Analysieren Sie, wie Preis- und Wachstumsänderungen das Ergebnis beeinflussen",
      priceVariation: "Preisvariation",
      growthVariation: "Wachstumsvariation",
      baseScenario: "Basisszenario",
      runAnalysis: "Analyse starten"
    },
    
    importExport: {
      importCsv: "CSV importieren",
      exportPdf: "PDF exportieren",
      dragDrop: "CSV-Datei hierher ziehen",
      orClick: "oder klicken um Datei auszuwählen",
      importSuccess: "Import erfolgreich",
      importError: "Importfehler"
    },
    
    common: {
      loading: "Wird geladen...",
      error: "Ein Fehler ist aufgetreten",
      success: "Fertig",
      save: "Speichern",
      cancel: "Abbrechen",
      delete: "Löschen",
      edit: "Bearbeiten",
      close: "Schließen",
      confirm: "Bestätigen",
      back: "Zurück",
      next: "Weiter",
      sek: "SEK",
      hectare: "ha",
      year: "Jahr",
      years: "Jahre",
      cubicMeter: "m³"
    },
    
    tooltips: {
      growthManaged: "Durchschnittlicher jährlicher Zuwachs bei optimaler Waldbewirtschaftung",
      growthNeglected: "Zuwachs ohne Pflege - typischerweise 20-30% niedriger",
      breakeven: "Zeitpunkt, an dem die Pflegekosten durch höhere Erträge zurückgezahlt wurden"
    }
  },
  
  en: {
    appName: "Skogskalkyl",
    appDescription: "Calculate what proper forest management is really worth",
    
    nav: {
      home: "Home",
      calculator: "Calculator",
      dashboard: "Dashboard",
      sensitivity: "Sensitivity Analysis",
      settings: "Settings",
      logout: "Logout",
      login: "Login",
      register: "Register",
      faq: "FAQ",
      about: "About"
    },
    
    auth: {
      email: "Email",
      password: "Password",
      name: "Name",
      loginTitle: "Sign in to your account",
      registerTitle: "Create an account",
      loginButton: "Sign in",
      registerButton: "Register",
      noAccount: "Don't have an account?",
      hasAccount: "Already have an account?",
      createAccount: "Create account",
      signIn: "Sign in"
    },
    
    calc: {
      title: "Forest Economy Calculator",
      subtitle: "Compare economic outcomes between managed and neglected forest",
      scenarioName: "Scenario name",
      region: "Region",
      area: "Area (hectares)",
      period: "Period (years)",
      treeSpecies: "Tree species",
      growthManaged: "Managed growth (m³/ha/year)",
      growthNeglected: "Neglected growth (m³/ha/year)",
      priceTimber: "Timber price (SEK/m³)",
      pricePulpwood: "Pulpwood price (SEK/m³)",
      clearingCost: "Clearing cost (SEK/ha)",
      plantingCost: "Planting cost (SEK/ha)",
      thinningCost: "Thinning cost (SEK/m³)",
      discountRate: "Discount rate (%)",
      calculate: "Calculate",
      preview: "Preview",
      saveScenario: "Save scenario",
      results: "Results",
      loadRegionalDefaults: "Load regional defaults"
    },
    
    risk: {
      title: "Risk scenarios",
      stormProbability: "Storm probability (%/year)",
      stormDamage: "Storm damage (% of volume)",
      barkBeetleProbability: "Bark beetle probability (%/year)",
      barkBeetleDamage: "Bark beetle damage (% of volume)",
      fireProbability: "Fire probability (%/year)",
      fireDamage: "Fire damage (% of volume)",
      enableRisk: "Enable risk calculation"
    },
    
    results: {
      title: "Calculation results",
      summary: "Summary",
      breakeven: "Break-even point",
      breakevenYear: "Break-even reached in year",
      totalBenefit: "Total net benefit",
      year: "Year",
      volume: "Volume",
      revenue: "Revenue",
      cost: "Cost",
      netBenefit: "Net benefit",
      cumulativeBenefit: "Cumulative benefit",
      npv: "Net present value",
      managedForest: "Managed forest",
      neglectedForest: "Neglected forest",
      extraVolume: "Extra volume",
      extraRevenue: "Extra revenue",
      exportPdf: "Export PDF",
      noBreakeven: "No break-even within period"
    },
    
    species: {
      gran: "Spruce (Picea abies)",
      tall: "Pine (Pinus sylvestris)",
      bjork: "Birch (Betula)"
    },
    
    regions: {
      norrland: "Norrland",
      svealand: "Svealand",
      gotaland: "Götaland"
    },
    
    dashboard: {
      title: "Your scenarios",
      noScenarios: "No saved scenarios",
      createFirst: "Create your first scenario",
      lastUpdated: "Last updated",
      delete: "Delete",
      view: "View",
      totalScenarios: "Total scenarios"
    },
    
    sensitivity: {
      title: "Sensitivity Analysis",
      description: "Analyze how price and growth changes affect the outcome",
      priceVariation: "Price variation",
      growthVariation: "Growth variation",
      baseScenario: "Base scenario",
      runAnalysis: "Run analysis"
    },
    
    importExport: {
      importCsv: "Import CSV",
      exportPdf: "Export PDF",
      dragDrop: "Drag and drop CSV file here",
      orClick: "or click to select file",
      importSuccess: "Import successful",
      importError: "Import error"
    },
    
    common: {
      loading: "Loading...",
      error: "An error occurred",
      success: "Done",
      save: "Save",
      cancel: "Cancel",
      delete: "Delete",
      edit: "Edit",
      close: "Close",
      confirm: "Confirm",
      back: "Back",
      next: "Next",
      sek: "SEK",
      hectare: "ha",
      year: "year",
      years: "years",
      cubicMeter: "m³"
    },
    
    tooltips: {
      growthManaged: "Average annual growth with optimal forest management (clearing, thinning)",
      growthNeglected: "Growth without management - typically 20-30% lower",
      breakeven: "Point when management costs have been repaid through higher returns"
    }
  },
  
  se: {
    // Northern Sami
    appName: "Skogskalkyl",
    appDescription: "Rehkenastte maid rievttes dikšun duođai lea veara",
    
    nav: {
      home: "Ruoktu",
      calculator: "Rehkenastin",
      dashboard: "Oppalašgovva",
      sensitivity: "Sensitiivvvuođaanalysa",
      settings: "Heivehusat",
      logout: "Olggosčálit",
      login: "Sisačálit",
      register: "Registreret",
      faq: "FAQ",
      about: "Min birra"
    },
    
    auth: {
      email: "E-poasta",
      password: "Beassansátni",
      name: "Namma",
      loginTitle: "Čále sisa du kontui",
      registerTitle: "Ráhkat konto",
      loginButton: "Čále sisa",
      registerButton: "Registreret",
      noAccount: "Ii leat konto?",
      hasAccount: "Lea go dus konto?",
      createAccount: "Ráhkat konto",
      signIn: "Čále sisa"
    },
    
    calc: {
      title: "Vuovdeekonomalaš rehkenastin",
      subtitle: "Buohtastahte ekonomalaš bohtosiid dikšojuvvon ja fuolakeahtes vuovddis",
      scenarioName: "Skenárijonamma",
      region: "Guovlu",
      area: "Viidodat (ha)",
      period: "Áigodat (jagit)",
      treeSpecies: "Muorašládja",
      growthManaged: "Šaddan dikšojuvvon (m³/ha/jahki)",
      growthNeglected: "Šaddan fuolakeahtes (m³/ha/jahki)",
      priceTimber: "Sáhkkobálkmuorra haddi (SEK/m³)",
      pricePulpwood: "Cellulosamuorra haddi (SEK/m³)",
      clearingCost: "Divungoasttádat (SEK/ha)",
      plantingCost: "Gilvomgoasttádat (SEK/ha)",
      thinningCost: "Čielgangoasttádat (SEK/m³)",
      discountRate: "Reantoproseantta (%)",
      calculate: "Rehkenastte",
      preview: "Ovdagovva",
      saveScenario: "Vurke skenárijona",
      results: "Bohtosat",
      loadRegionalDefaults: "Viečča guovlodieđuid"
    },
    
    risk: {
      title: "Riskaskenárijot",
      stormProbability: "Árásiidvejolašvuohta (%/jahki)",
      stormDamage: "Árásiidvahágat (% volumenis)",
      barkBeetleProbability: "Bárkalillevejolašvuohta (%/jahki)",
      barkBeetleDamage: "Bárkalillevahágat (% volumenis)",
      fireProbability: "Dollarisku (%/jahki)",
      fireDamage: "Dollavahágat (% volumenis)",
      enableRisk: "Aktivere riskarehkenastima"
    },
    
    results: {
      title: "Rehkenastinbohtosat",
      summary: "Čoahkkáigeassu",
      breakeven: "Dássemearri",
      breakevenYear: "Dássemearri olahuvvo jagis",
      totalBenefit: "Ollislaš neahttaávki",
      year: "Jahki",
      volume: "Volumen",
      revenue: "Boađus",
      cost: "Goasttádat",
      netBenefit: "Neahttaávki",
      cumulativeBenefit: "Čoahkkáiduvvon ávki",
      npv: "Dáláárvvu",
      managedForest: "Dikšojuvvon vuovdi",
      neglectedForest: "Fuolakeahtes vuovdi",
      extraVolume: "Lasi volumen",
      extraRevenue: "Lasi boađus",
      exportPdf: "Olggosfievrrit PDF",
      noBreakeven: "Ii dássemearri áigodagas"
    },
    
    species: {
      gran: "Skierri (Picea abies)",
      tall: "Biehtse (Pinus sylvestris)",
      bjork: "Soahki (Betula)"
    },
    
    regions: {
      norrland: "Norrland",
      svealand: "Svealand",
      gotaland: "Götaland"
    },
    
    dashboard: {
      title: "Du skenárijot",
      noScenarios: "Eai vurkon skenárijot",
      createFirst: "Ráhkat vuosttaš skenárijona",
      lastUpdated: "Maŋimustá ođasmahtton",
      delete: "Sihko",
      view: "Geahča",
      totalScenarios: "Ollislaš skenárijot"
    },
    
    sensitivity: {
      title: "Sensitiivvvuođaanalysa",
      description: "Analyseree mo hadde- ja šaddanrievdamat váikkuhit bohtosiid",
      priceVariation: "Hadderievdan",
      growthVariation: "Šaddanrievdan",
      baseScenario: "Vuođđoskenárijo",
      runAnalysis: "Jođiha analysa"
    },
    
    importExport: {
      importCsv: "Sisafierra CSV",
      exportPdf: "Olggosfievrrit PDF",
      dragDrop: "Gease ja luoitte CSV-fiilla dása",
      orClick: "dahje coahkkal válljet fiilla",
      importSuccess: "Sisafierran lihkostuvai",
      importError: "Sisafierranmeattáhus"
    },
    
    common: {
      loading: "Viečahallamin...",
      error: "Meattáhus dáhpáhuvai",
      success: "Gergen",
      save: "Vurke",
      cancel: "Heaitte",
      delete: "Sihko",
      edit: "Rievdat",
      close: "Gidde",
      confirm: "Duođašt",
      back: "Ruoktot",
      next: "Čuovvovaš",
      sek: "SEK",
      hectare: "ha",
      year: "jahki",
      years: "jagit",
      cubicMeter: "m³"
    },
    
    tooltips: {
      growthManaged: "Gaskaárvu jahkásaš šaddan buoremus vuovdedikšumiin",
      growthNeglected: "Šaddan dikšuma haga - dábálaččat 20-30% vuollelis",
      breakeven: "Áigemearri goas dikšungoasttádat leat máhccán alit boađuin"
    }
  }
};

export const getTranslation = (lang, key) => {
  const keys = key.split('.');
  let value = translations[lang] || translations.sv;
  
  for (const k of keys) {
    if (value && typeof value === 'object') {
      value = value[k];
    } else {
      return key;
    }
  }
  
  return value || key;
};

export const languages = [
  { code: 'sv', name: 'Svenska', flag: '🇸🇪' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'se', name: 'Sámegiella', flag: '🏳️' }
];
